<template>
    <div>
        <div class="card d-lg-block" :class="{ 'd-block': hasActiveConversation, 'd-none': !hasActiveConversation}">
            <div>
                <div class="card-header bg-primary-darkest text-white rounded-0">
                    <div class="row">
                        <div class="col-2 text-center align-self-center d-lg-none d-block">
                            <div @click="resetActiveConversation">
                                <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </div>
                        </div>
                        <div class="col-10">
                            <h4 class="mb-0 px-3 text-base" v-html="conversationName"></h4>
                        </div>
                    </div>
                </div>
                <div class="alert alert-warning rounded-0 mb-0 text-center" v-if="conversationStatus && isEnabled" data-bs-toggle="tooltip" data-placement="bottom" :title="conversationStatus">
                    {{ conversationStatus }}
                </div>
                <div class="alert alert-danger rounded-0 mb-0 text-center" v-if="!isEnabled" data-bs-toggle="tooltip" data-placement="bottom" :title="$t('Lærer kan ikke lenger motta meldinger')">
                    {{ $t('Lærer kan ikke lenger motta meldinger') }}
                </div>
                <div class="text-center col-12 py-3 card-body box-height" v-show="isLoading">
                    <span class="spinner-border" role="status"></span>
                </div>
                <div class="card-body box-height" ref="conversationBody" v-show="!isLoading" :class="{'has-status': hasStatus}">
                    <div class="text-center text-sm" style="opacity: 0.8;" v-if="hasActiveConversation && !messages.length">
                        {{ $t('Ingen meldinger sendt.') }}
                    </div>
                    <Message v-for="(message, i) in messages" :message="message" :conversation="conversation" :previousMessage="messages[i-1]" :key="message.id" @loaded="scrollToBottom"></Message>
                </div>
                <div class="card-footer bg-body">
                    <div class="row no-gutters">
                        <div class="col-10">
                            <input type="text"
                                   class="form-control"
                                   :disabled="!hasActiveConversation || isSending || isLoading || !isEnabled"
                                   v-model="newMessage"
                                   autofocus
                                   @keyup.enter.prevent="sendMessage"
                                   :placeholder="isEnabled ? $t('Skriv en melding') : $t('Samtalen er avsluttet')" />
                        </div>
                        <div class="col-2 align-self-center">
                            <span class="text-elev-blue d-block text-center cursor-pointer" @click="sendMessage"
                                  :disabled="isSending || isLoading || !hasActiveConversation || !isEnabled">
                                <i class="ml-2 fa fa-paper-plane" v-show="!isSending"></i>
                                <span class="fa fa-spin fa-spinner" v-show="isSending"></span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Store from '../../state/chatStore';
import Message from './Message';

const newMessage = ref('');

const conversationBody = ref(null);

const office = computed(() => Store.getActiveConversation() ? Store.getOffice() : null);
const teacher = computed(() => Store.getActiveConversation() ? Store.getActiveConversation().teacher : null);
const conversationName = computed(() => {
    if (teacher.value) {
        return '<strong>' + teacher.value.full_call_name + '</strong><br>' + $t('Trafikklærer') + ', ' + office.value.office_name;
    }
    if (office.value) {
        return '<p class="mb-4"><strong>' + office.value.office_name + '</strong></p>';
    }
    return '<p class="mb-4"><strong>' + $t('Velg samtale') + '</strong></p>';
});
const hasStatus = computed(() => conversationStatus.value.length > 0);
const conversationStatus = computed(() => Store.getConversationStatus());
const isLoading = computed(() => Store.isLoadingConversation());
const isSending = computed(() => Store.isSending());
const messages = computed(() => Store.getMessages());
const hasActiveConversation = computed(() => Store.getActiveConversation() !== null);
const conversation = computed(() => Store.getActiveConversation());
const isEnabled = computed(() => {
    if (hasActiveConversation.value) {
        return conversation.value.enabled;
    }
    return true;
});

const sendMessage = () => {
    if (isSending.value || newMessage.value.length === 0) {
        // Already sending
        return;
    }
    Store.sendMessage(newMessage.value).then(() => {
        newMessage.value = '';
    }).catch(() => {
        newMessage.value = '';
    });
};

const scrollToBottom = () => {
    setTimeout(() => {
        const tooltipElements = document.querySelectorAll('[data-toggle="tooltip"]');
        for (let el of tooltipElements) {
            new BootstrapNative.Tooltip(el, {});
        }

        if (!conversationBody.value) {
            return;
        }
        const conversationBodyEl = conversationBody.value;
        conversationBodyEl.scrollTop = conversationBodyEl.scrollHeight;
    }, 10);
};

const resetActiveConversation = () => {
    Store.resetActiveConversation();
};

watch(isLoading, () => {
    if (isLoading.value) {
        return;
    }
    scrollToBottom();
});

watch(messages, () => {
    scrollToBottom();
});
</script>

<style scoped>
    .input-group-btn.btn.btn-primary[disabled] {
        cursor: not-allowed;
    }
    .box-height {
        max-height: calc(100vh - 163px);
        height: 1000px;
        overflow-y: scroll;
        overflow-x: hidden;
    }
    .box-height.has-status {
        max-height: calc(100vh - 211px);
        height: 950px;
    }

    .alert {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .card-footer {
      padding-bottom: 32px;
    }

    @media (min-width: 992px) {

        .box-height.has-status {
            height: 450px;
        }
        .box-height {
            height: 500px;
        }
    }
</style>